// File: src/App.tsx
import React, { Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { RequireAuth } from './components/RequireAuth';
import { OnboardingGate } from './components/OnboardingGate';
import { ToastContainer } from './components/ui/Toast';
import { CommandPalette } from './components/CommandPalette';
import { SubscriptionUpgradeListener } from './components/SubscriptionUpgradeListener';
import { GracePeriodNotifier } from './components/GracePeriodNotifier';
import { TermsChecker } from './components/TermsChecker';
import { FeedbackWidget } from './components/FeedbackWidget';
import { AppErrorBoundary } from './components/ErrorBoundary';
import { usePageTracking } from './hooks/usePageTracking';
import { lazyWithRetry } from './lib/lazyWithRetry';

// Layout (eagerly loaded — used on every authenticated page)
import { Layout } from './components/Layout';

// Lazy-loaded page components
const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'));
const FAQPage = lazyWithRetry(() => import('./pages/FAQPage'));
const LoginPage = lazyWithRetry(() => import('./pages/LoginPage'));
const AccountPage = lazyWithRetry(() => import('./pages/AccountPage'));
const OnboardingPage = lazyWithRetry(() => import('./pages/OnboardingPage'));
const UpgradePage = lazyWithRetry(() =>
  import('./pages/UpgradePage').then((m) => ({ default: m.UpgradePage }))
);
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage'));
const TransactionsPageV2 = lazyWithRetry(() => import('./pages/TransactionsPageV2'));
const TransactionDetailPageV2 = lazyWithRetry(() => import('./pages/TransactionDetailPageV2'));
const InsidersPageV2 = lazyWithRetry(() => import('./pages/InsidersPageV2'));
const InsiderDetailPageV2 = lazyWithRetry(() => import('./pages/InsiderDetailPageV2'));
const CompanyListPageV2 = lazyWithRetry(() => import('./pages/CompanyListPageV2'));
const CompanyDetailPageV2 = lazyWithRetry(() => import('./pages/CompanyDetailPageV2'));
const AlertsPageV2 = lazyWithRetry(() => import('./pages/AlertsPageV2'));
const InvestorsPage = lazyWithRetry(() => import('./pages/InvestorsPage'));
const PortfolioPage = lazyWithRetry(() => import('./pages/PortfolioPage'));
const InvestorDetailPage = lazyWithRetry(() => import('./pages/InvestorDetailPage'));
const NotFoundPage = lazyWithRetry(() => import('./pages/NotFoundPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes cache
      retry: 3,
    },
  },
});

// Component to track page views - must be inside Router
function PageTracker() {
  usePageTracking();
  return null;
}

// Redirect helpers for parameterized /v2/* routes
function RedirectV2Transaction() {
  const { id } = useParams();
  return <Navigate to={`/transactions/${id}`} replace />;
}
function RedirectV2Insider() {
  const { cik } = useParams();
  return <Navigate to={`/insiders/${cik}`} replace />;
}
function RedirectV2Company() {
  const { ticker } = useParams();
  return <Navigate to={`/companies/${ticker}`} replace />;
}

function App() {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <PageTracker />
          <CommandPalette />
          <ToastContainer />
          <SubscriptionUpgradeListener />
          <GracePeriodNotifier />
          <TermsChecker />
          <FeedbackWidget />
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<Navigate to="/login" replace />} />
              <Route path="/pricing" element={<Navigate to="/upgrade" replace />} />

              {/* Canonical routes with Layout */}
              <Route
                element={
                  <RequireAuth>
                    <OnboardingGate>
                      <Layout />
                    </OnboardingGate>
                  </RequireAuth>
                }
              >
                <Route path="/onboarding" element={<OnboardingPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/transactions" element={<TransactionsPageV2 />} />
                <Route path="/transactions/:id" element={<TransactionDetailPageV2 />} />
                <Route path="/insiders" element={<InsidersPageV2 />} />
                <Route path="/insiders/:cik" element={<InsiderDetailPageV2 />} />
                <Route path="/companies" element={<CompanyListPageV2 />} />
                <Route path="/companies/:ticker" element={<CompanyDetailPageV2 />} />
                <Route path="/investors" element={<InvestorsPage />} />
                <Route path="/investors/:cik" element={<InvestorDetailPage />} />
                <Route path="/alerts" element={<AlertsPageV2 />} />
                <Route
                  path="/portfolio"
                  element={
                    <RequireAuth requireStaff>
                      <PortfolioPage />
                    </RequireAuth>
                  }
                />
                <Route path="/settings" element={<AccountPage />} />
                <Route path="/upgrade" element={<UpgradePage />} />
              </Route>

              {/* Redirects from old /v2/* paths to canonical equivalents */}
              <Route path="/notables" element={<Navigate to="/dashboard" replace />} />
              <Route path="/v2/transactions" element={<Navigate to="/transactions" replace />} />
              <Route path="/v2/transactions/:id" element={<RedirectV2Transaction />} />
              <Route path="/v2/insiders" element={<Navigate to="/insiders" replace />} />
              <Route path="/v2/insiders/:cik" element={<RedirectV2Insider />} />
              <Route path="/v2/companies" element={<Navigate to="/companies" replace />} />
              <Route path="/v2/companies/:ticker" element={<RedirectV2Company />} />
              <Route path="/v2/alerts" element={<Navigate to="/alerts" replace />} />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Router>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}

export default App;
